import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from "react";
import type { GriddEmbedHandle, GriddSnapshot } from "@gridd/embed/react";

// A registry of live Gridd embed handles keyed by griddDocId (TODO §6.6.4). Just before
// print, the export flow pulls a static snapshot of each *editor* grid — the ones already
// mounted and painted — so the print layer can render `<img>`s instead of re-mounting
// cross-origin iframes that don't print reliably (§6.3).
//
// The editor tree provides `register` through context; every mounted `<GriddEmbed>`
// registers its imperative handle (1.3.0's `GriddEmbedHandle`). `requestSnapshots` fans out
// over the wanted ids and tolerates per-grid failure, so one hung or unregistered grid can
// never stall the whole export (§6.6.5).

type RegisterFn = (griddDocId: string, handle: GriddEmbedHandle | null) => void;

// Pull static snapshots of the named live embeds. `opts.scale` is the DPR multiplier passed
// to each embed (default 2 for crisp print/present); the thumbnail store passes a small scale
// for cheap nav previews.
export type RequestSnapshots = (
  griddDocIds: string[],
  opts?: { scale?: number },
) => Promise<Record<string, GriddSnapshot>>;

const RegisterContext = createContext<RegisterFn | null>(null);

export function useSnapshotRegistry() {
  const handles = useRef(new Map<string, GriddEmbedHandle>());

  const register = useCallback<RegisterFn>((griddDocId, handle) => {
    if (handle) handles.current.set(griddDocId, handle);
    else handles.current.delete(griddDocId);
  }, []);

  // Snapshot each id in parallel. Missing/failed grids are dropped (and logged) rather than
  // rejecting the batch, so the caller gets whatever succeeded and the print path falls back
  // to the live iframe for the rest (§6.6.5). `scale` defaults to 2 (keeps a 1600×900 page
  // crisp); nav thumbnails pass a small scale for a cheap, tiny PNG.
  const requestSnapshots = useCallback<RequestSnapshots>(
    async (griddDocIds, opts) => {
      const scale = opts?.scale ?? 2;
      const settled = await Promise.allSettled(
        griddDocIds.map(async (id) => {
          const handle = handles.current.get(id);
          if (!handle) throw new Error(`no live embed registered for ${id}`);
          return [id, await handle.requestSnapshot({ scale })] as const;
        }),
      );
      const out: Record<string, GriddSnapshot> = {};
      for (const r of settled) {
        if (r.status === "fulfilled") out[r.value[0]] = r.value[1];
        else console.warn("[gridd-snapshot]", r.reason);
      }
      return out;
    },
    [],
  );

  return { register, requestSnapshots };
}

export function SnapshotRegisterProvider({
  register,
  children,
}: {
  register: RegisterFn;
  children: ReactNode;
}) {
  return (
    <RegisterContext.Provider value={register}>
      {children}
    </RegisterContext.Provider>
  );
}

// Callback ref for a `<GriddEmbed>`: registers the embed's imperative handle by griddDocId
// on mount and clears it on unmount / id change. No-op outside the provider (e.g. the print
// copies — which render snapshots, not embeds, so they never call this anyway). Stable per
// griddDocId, so React only re-registers when the id actually changes.
export function useSnapshotSourceRef(griddDocId: string | null) {
  const register = useContext(RegisterContext);
  return useCallback(
    (handle: GriddEmbedHandle | null) => {
      if (!register || !griddDocId) return;
      register(griddDocId, handle);
    },
    [register, griddDocId],
  );
}
